import { useEffect, useState } from 'react';
import './MatchContextModal.css';
import './TennisContextModal.css';

const SURFACE_COLOR = { Hard: '#3b82f6', Clay: '#f97316', Grass: '#22c55e' };

function ResultBadge({ result }) {
  const bg = result === 'W' ? '#16a34a' : '#ef4444';
  return <span className="mcm-badge" style={{ background: bg }}>{result}</span>;
}

function FormRow({ player, form }) {
  return (
    <div className="mcm-form-row">
      <span className="mcm-form-row__team">{player}</span>
      <div className="mcm-form-row__badges">
        {form.summary.form_string.split('').map((r, i) => (
          <ResultBadge key={i} result={r} />
        ))}
        {!form.summary.form_string && (
          <span className="mcm-empty-text">Sin partidos recientes</span>
        )}
      </div>
      <span className="mcm-form-row__pts">
        {form.summary.wins}V / {form.summary.losses}D
      </span>
    </div>
  );
}

function MiniMatch({ match, player }) {
  return (
    <div className={`mcm-mini mcm-mini--${match.result === 'W' ? 'win' : 'loss'}`}>
      <span className="mcm-mini__date">{match.date}</span>
      <span className="tcm-surface" style={{ background: SURFACE_COLOR[match.surface] || '#6b7280' }}>
        {match.surface?.[0]}
      </span>
      <span className="mcm-mini__opp">{match.opponent}</span>
      {match.score && <span className="mcm-mini__score tcm-score">{match.score}</span>}
      <ResultBadge result={match.result} />
    </div>
  );
}

function H2HMatch({ match, p1Name }) {
  const isP1 = match.winner === p1Name;
  return (
    <div className="mcm-mini mcm-mini--decided">
      <span className="mcm-mini__date">{match.date}</span>
      <span className="tcm-surface" style={{ background: SURFACE_COLOR[match.surface] || '#6b7280' }}>
        {match.surface?.[0]}
      </span>
      <span className="mcm-mini__opp mcm-mini__opp--h2h">
        <span className={isP1 ? 'mcm-h2h-winner' : 'mcm-h2h-loser'}>{p1Name}</span>
        <em>vs</em>
        <span className={!isP1 ? 'mcm-h2h-winner' : 'mcm-h2h-loser'}>
          {match.winner === p1Name
            ? match.tournament  // fallback if no p2 name
            : match.winner}
        </span>
      </span>
      {match.score && <span className="mcm-mini__score tcm-score">{match.score}</span>}
      <span className="mcm-h2h-winner-badge">{match.winner?.split(' ').pop()}</span>
    </div>
  );
}

export function TennisContextModal({ pick, onClose }) {
  const [ctx, setCtx]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('forma');
  const API = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (!pick?.match_id) return;
    setLoading(true);
    setCtx(null);
    setTab('forma');
    fetch(`${API}/tennis/matches/${pick.match_id}/context`)
      .then(r => r.json())
      .then(setCtx)
      .finally(() => setLoading(false));
  }, [pick?.match_id]);

  if (!pick) return null;

  const surfaceColor = SURFACE_COLOR[pick.surface] || '#6b7280';
  const prob1 = Math.round(pick.prob_winner * 100);
  const prob2 = Math.round(pick.prob_underdog * 100);
  const isP1Winner = pick.predicted_winner === pick.p1_name;

  return (
    <div className="mcm-overlay" onClick={onClose}>
      <div className="mcm-box" onClick={e => e.stopPropagation()}>

        {/* HEADER */}
        <div className="mcm-header">
          <div className="mcm-header__league">
            {pick.tournament}
            <span className="tcm-surface-pill" style={{ background: surfaceColor }}>
              {pick.surface}
            </span>
          </div>
          <button className="mcm-close" onClick={onClose}>✕</button>
        </div>

        {/* JUGADORES */}
        <div className="mcm-teams">
          <div className={`mcm-team mcm-team--home ${isP1Winner ? 'tcm-predicted' : ''}`}>
            {pick.p1_name}
          </div>
          <div className="mcm-team-sep">vs</div>
          <div className={`mcm-team mcm-team--away ${!isP1Winner ? 'tcm-predicted' : ''}`}>
            {pick.p2_name}
          </div>
        </div>
        <div className="mcm-date">{pick.date}</div>

        {/* BARRA DE PROBABILIDADES */}
        <div className="mcm-pred-strip">
          <div className="mcm-pred-bar">
            <div className="mcm-pred-bar__seg" style={{ width: `${isP1Winner ? prob1 : prob2}%`, background: '#3b82f6' }} />
            <div className="mcm-pred-bar__seg" style={{ width: `${isP1Winner ? prob2 : prob1}%`, background: '#ef4444' }} />
          </div>
          <div className="mcm-pred-labels">
            <span><span className="mcm-pred-dot" style={{ background: '#3b82f6' }} />{pick.p1_name} {isP1Winner ? prob1 : prob2}%</span>
            <span><span className="mcm-pred-dot" style={{ background: '#ef4444' }} />{pick.p2_name} {isP1Winner ? prob2 : prob1}%</span>
          </div>
          <div className="mcm-pred-conclusion">
            <span>Prediccion:</span>
            <strong>{pick.predicted_winner}</strong>
            <span className="mcm-conf-badge">{Math.round(pick.confidence * 100)}% confianza</span>
          </div>
        </div>

        {/* TABS */}
        <div className="mcm-tabs">
          {['forma', 'h2h'].map(t => (
            <button key={t} className={`mcm-tab ${tab === t ? 'mcm-tab--active' : ''}`}
                    onClick={() => setTab(t)}>
              {t === 'forma' ? 'Forma reciente' : 'H2H'}
            </button>
          ))}
        </div>

        <div className="mcm-body">
          {loading && <p className="mcm-loading">Cargando estadisticas...</p>}
          {!loading && !ctx && <p className="mcm-empty">Sin datos disponibles</p>}

          {/* FORMA */}
          {!loading && ctx && tab === 'forma' && (
            <>
              <div className="mcm-form-summary">
                <FormRow player={ctx.p1_name} form={ctx.p1_form} />
                <FormRow player={ctx.p2_name} form={ctx.p2_form} />
              </div>
              <div className="mcm-block">
                <h4 className="mcm-block__title">{ctx.p1_name} — Ultimos partidos</h4>
                {ctx.p1_form.matches.length === 0
                  ? <p className="mcm-empty">Sin historial disponible</p>
                  : ctx.p1_form.matches.map((m, i) => (
                      <MiniMatch key={i} match={m} player={ctx.p1_name} />
                    ))}
              </div>
              <div className="mcm-block">
                <h4 className="mcm-block__title">{ctx.p2_name} — Ultimos partidos</h4>
                {ctx.p2_form.matches.length === 0
                  ? <p className="mcm-empty">Sin historial disponible</p>
                  : ctx.p2_form.matches.map((m, i) => (
                      <MiniMatch key={i} match={m} player={ctx.p2_name} />
                    ))}
              </div>
            </>
          )}

          {/* H2H */}
          {!loading && ctx && tab === 'h2h' && (
            <>
              <div className="mcm-h2h-summary">
                <div className="mcm-h2h-col">
                  <span className="mcm-h2h-num">{ctx.h2h.p1_wins}</span>
                  <span className="mcm-h2h-label">{ctx.p1_name}</span>
                </div>
                <div className="mcm-h2h-col mcm-h2h-col--draw">
                  <span className="mcm-h2h-num">{ctx.h2h.p1_wins + ctx.h2h.p2_wins}</span>
                  <span className="mcm-h2h-label">total</span>
                </div>
                <div className="mcm-h2h-col">
                  <span className="mcm-h2h-num">{ctx.h2h.p2_wins}</span>
                  <span className="mcm-h2h-label">{ctx.p2_name}</span>
                </div>
              </div>
              <div className="mcm-block">
                <h4 className="mcm-block__title">Historial directo</h4>
                {ctx.h2h.matches.length === 0
                  ? <p className="mcm-empty">Sin enfrentamientos previos</p>
                  : ctx.h2h.matches.map((m, i) => (
                      <H2HMatch key={i} match={m} p1Name={ctx.p1_name} />
                    ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
